import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/money";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Btn, Empty, StatusPill, dialogCls } from "@/components/admin/ui";
import { InvoiceEditor } from "@/components/admin/InvoiceEditor";

const blank = (client_id = "") => ({ client_id, title: "", due_date: "", notes: "", deposit_type: "percent", deposit_value: 50, amount_paid: 0, status: "draft", line_items: [{ description: "", quantity: 1, unit_price: 0 }] });

export const InvoicesPanel = ({ clients, openNewFor, clearOpenNew }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/admin/invoices").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (openNewFor) {
      setEditing(blank(openNewFor));
      clearOpenNew();
    }
  }, [openNewFor, clearOpenNew]);

  const onSaved = (inv) => setItems((xs) => (xs.some((x) => x.id === inv.id) ? xs.map((x) => (x.id === inv.id ? { ...x, ...inv } : x)) : [inv, ...xs]));

  const act = async (inv, action) => {
    if (!window.confirm(action === "void" ? "Void this invoice? The client link will stop working." : "Delete this invoice permanently?")) return;
    try {
      if (action === "void") await api.post(`/admin/invoices/${inv.id}/void`);
      else await api.delete(`/admin/invoices/${inv.id}`);
      setItems((xs) => (action === "void" ? xs.map((x) => (x.id === inv.id ? { ...x, status: "void" } : x)) : xs.filter((x) => x.id !== inv.id)));
      setEditing(null);
      toast.success(action === "void" ? "Invoice voided" : "Invoice deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="invoices-panel">
      <div className="mb-4 flex justify-end">
        <Btn variant="primary" onClick={() => setEditing(blank())} disabled={!clients.length} data-testid="invoice-new-btn"><Plus className="h-4 w-4" /> New invoice</Btn>
      </div>
      {!items.length ? <Empty testId="invoices-empty">No invoices yet. {clients.length ? "Create one for a client." : "Add a client first."}</Empty> : (
        <div className="divide-y divide-[#2E2A24] overflow-hidden rounded-2xl border border-[#2E2A24]">
          {items.map((inv) => (
            <div key={inv.id} className="flex items-center gap-2 bg-[#121215] pr-3 hover:bg-[#1A1A20]">
              <button onClick={() => setEditing(inv)} className="grid min-w-0 flex-1 grid-cols-1 gap-1 px-5 py-4 text-left sm:grid-cols-12 sm:items-center" data-testid={`invoice-row-${inv.id}`}>
                <span className="sm:col-span-4"><span className="block font-medium text-[#F5EFE6]">{inv.title}</span><span className="font-mono-rc text-[11px] text-zinc-400">{inv.number}</span></span>
                <span className="text-sm text-zinc-300 sm:col-span-3">{inv.client_name}</span>
                <span className="text-sm text-zinc-300 sm:col-span-2">{money(inv.total)}{inv.amount_paid > 0 && <span className="block text-xs text-emerald-300">Paid {money(inv.amount_paid)}</span>}</span>
                <span className="text-xs text-zinc-400 sm:col-span-1">{fmtDate(inv.created_at)}</span>
                <span className="sm:col-span-2 sm:text-right"><StatusPill status={inv.status} /></span>
              </button>
              {!(inv.amount_paid > 0) ? (
                <Btn variant="danger" onClick={() => act(inv, "delete")} aria-label="Delete invoice" data-testid={`invoice-row-delete-${inv.id}`}><Trash2 className="h-4 w-4" /></Btn>
              ) : <span className="w-12" title="Paid invoices can't be deleted" />}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing?.id ? editing.title : "New invoice"}</DialogTitle></DialogHeader>
          {editing && <InvoiceEditor key={editing.id || "new"} initial={editing} clients={clients} onSaved={onSaved} />}
          {editing?.id && (
            <div className="flex gap-4 border-t border-[#2E2A24] pt-4">
              {editing.status !== "void" && <Btn variant="danger" onClick={() => act(editing, "void")} data-testid="invoice-void-btn">Void</Btn>}
              {!(editing.amount_paid > 0) && <Btn variant="danger" onClick={() => act(editing, "delete")} data-testid="invoice-delete-btn">Delete</Btn>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
